import { AnalyticsPlugin } from "analytics";
import GA4React from "ga-4-react";

type ProviderConfig = {
  id: string;
  enabled: boolean;
  debug_mode: boolean;
};

type InitializeData = {
  config: ProviderConfig;
};

interface PageDataBase {
  title?: string;
  url?: string;
  path?: string;
  search?: string;
  width?: string;
  height?: string;
}

type PageData<T extends string = string> = PageDataBase & Record<T, unknown>;

type PluginType = {
  name: string;
  config: ProviderConfig;
  initialize(data: InitializeData): Promise<void>;
  loaded(): boolean;
  page(data?: PageData): Promise<any>;
  track(eventName: string, payload?: any): Promise<void>;
};

export default function GA4Provider(
  userConfig: ProviderConfig
): AnalyticsPlugin<"ga4"> {
  let client: GA4React | null = null;
  let loaded = false;

  // return object for analytics to use
  const plugin: PluginType = {
    /* All plugins require a name */
    name: "ga4",
    config: userConfig,
    initialize: ({ config }: { config: ProviderConfig }) => {
      client = new GA4React(config.id, {
        debug_mode: config.debug_mode,
      });
      return client.initialize().then(() => {
        loaded = true;
      });
    },

    page: (event) => {
      // call provider specific page tracking
      client?.pageview(event?.path || window.location);
      return Promise.resolve();
    },

    track: (eventName) => {
      if (eventName) {
        // call provider specific event tracking
        client?.event(eventName, "event", "track");
      }

      return Promise.resolve();
    },

    loaded: () => {
      // return boolean so analytics knows when it can send data to third party
      return !!loaded;
    },
  };

  // This conversion is needed because the AnalyticsPlugin type defined
  // uses any everywhere. That's why i've duplicated some of the (internal)
  // data types
  return plugin as any as AnalyticsPlugin;
}
